import { attr, computedOf, Fragment, html, on, Value, When } from '@tempots/dom'
import { Anchor, HTMLTitle } from '@tempots/ui'
import { Logo } from '@/components/logo'
import { MarkdownSection } from '@/components/markdown-section'
import { Track } from '@/services/track'
import { Copyright } from './copyright'
import { HR } from './hr'
import { Section } from './section'

export interface InstitutionalPageOptions {
  /** Page title for HTMLTitle (e.g., "About - ResumeHeaders") */
  title: Value<string>
  /** Optional heading text to display (e.g., "About ResumeHeaders") */
  heading?: Value<string>
  /** URL to the markdown content file */
  markdownUrl: Value<string>
  /** Background color for the markdown section */
  background?: Value<'white' | 'light' | 'dark'>
  /** Whether to show the header with logo and heading */
  showHeader?: Value<boolean>
  /** Whether to show the footer with contact info and links */
  showFooter?: Value<boolean>
}

/**
 * A reusable component for institutional pages (About, Terms, Contact, etc.)
 * that displays markdown content with optional header and footer sections.
 */
export function InstitutionalPage({
  title,
  heading,
  markdownUrl,
  background = 'light',
  showHeader = false,
  showFooter = false,
}: InstitutionalPageOptions) {
  const displayHeader = computedOf(
    showHeader,
    heading
  )((show, heading) => {
    return show && heading != null
  })
  return Fragment(
    HTMLTitle(title),
    html.div(
      attr.class(
        'flex flex-col max-h-full overflow-y-auto min-h-full bg-gradient-to-b from-gray-50 to-white'
      ),
      When(displayHeader, () =>
        html.header(
          attr.class(
            'flex flex-col items-center gap-6 py-8 px-4 bg-white border-b border-gray-200 shadow-sm'
          ),
          Anchor(
            { href: '/' },
            on.click(() =>
              Track.click({
                section: 'header',
                element_type: 'link',
                click_action: 'navigate',
                element_id: 'home',
              })
            ),
            Logo({ size: 'header' })
          ),
          html.h1(
            attr.class(
              'text-3xl sm:text-4xl font-bold text-gray-900 text-center'
            ),
            heading
          )
        )
      ),
      html.div(
        attr.class('flex-grow'),
        MarkdownSection({
          url: markdownUrl,
          background,
        })
      ),
      When(showFooter, () =>
        Section(
          { background: 'dark' },
          attr.class('text-center flex flex-col gap-6 mt-8'),
          html.div(
            attr.class('flex flex-col gap-3'),
            html.p(
              attr.class('text-base text-gray-300 font-medium'),
              'ResumeHeaders is built by recruiters, for recruiters.'
            ),
            html.p(
              attr.class('text-sm text-gray-400'),
              'If you have any questions, please contact us at ',
              html.a(
                attr.href('mailto:support@resumeheaders.com'),
                attr.class('text-teal-400 hover:text-teal-300 underline'),
                on.click(() =>
                  Track.click({
                    section: 'footer',
                    element_type: 'link',
                    click_action: 'navigate',
                    element_id: 'contact_support',
                  })
                ),
                'support@resumeheaders.com'
              ),
              '.'
            )
          ),
          html.nav(
            attr.class(
              'flex flex-row flex-wrap items-center gap-3 justify-center text-sm'
            ),
            Anchor(
              { href: '/terms' },
              attr.class(
                'text-gray-300 hover:text-white transition-colors duration-200'
              ),
              on.click(() =>
                Track.click({
                  section: 'footer',
                  element_type: 'link',
                  click_action: 'navigate',
                  element_id: 'terms',
                })
              ),
              'Terms of Service'
            ),
            html.span(attr.class('text-gray-600'), '•'),
            Anchor(
              { href: '/faq' },
              attr.class(
                'text-gray-300 hover:text-white transition-colors duration-200'
              ),
              on.click(() =>
                Track.click({
                  section: 'footer',
                  element_type: 'link',
                  click_action: 'navigate',
                  element_id: 'faq',
                })
              ),
              'FAQ & Documentation'
            ),
            html.span(attr.class('text-gray-600'), '•'),
            Anchor(
              { href: '/contact' },
              attr.class(
                'text-gray-300 hover:text-white transition-colors duration-200'
              ),
              on.click(() =>
                Track.click({
                  section: 'footer',
                  element_type: 'link',
                  click_action: 'navigate',
                  element_id: 'contact',
                })
              ),
              'Contact Us'
            ),
            html.span(attr.class('text-gray-600'), '•'),
            Anchor(
              { href: '/about' },
              attr.class(
                'text-gray-300 hover:text-white transition-colors duration-200'
              ),
              on.click(() =>
                Track.click({
                  section: 'footer',
                  element_type: 'link',
                  click_action: 'navigate',
                  element_id: 'about',
                })
              ),
              'About'
            ),
            html.span(attr.class('text-gray-600'), '•'),
            Anchor(
              { href: '/privacy' },
              attr.class(
                'text-gray-300 hover:text-white transition-colors duration-200'
              ),
              on.click(() =>
                Track.click({
                  section: 'footer',
                  element_type: 'link',
                  click_action: 'navigate',
                  element_id: 'privacy',
                })
              ),
              'Privacy Policy'
            )
          ),
          HR(),
          Copyright()
        )
      )
    )
  )
}
